import React, { useRef, useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';

const VideoContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  justify-content: center;
  align-items: center;
  /* 배경색 제거: AppLayout의 배경이 그대로 보이게 */
  background: transparent;
  
  @media (max-width: 768px) {
    width: 100vw;
    height: calc(var(--vh, 1vh) * 100);
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
  }
`;

const PlayerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 370px;
  max-height: 700px;
  aspect-ratio: 9/16;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.5s ease-in-out, opacity 0.5s;
  
  &.minimized {
    position: absolute;
    top: 20px;
    right: 0;
    width: 115px;
    height: 200px;
    max-width: 120px;
    max-height: 200px;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border-radius: 8px;
    opacity: 0.4;
    aspect-ratio: 9/16;
  }
  
  @media (max-width: 768px) {
    /* max-width: 70vw; */
    aspect-ratio: 9 / 16;
    width: auto;
    max-height: 60vh;

    &.minimized {
      width: 115px;
      height: 200px;
      max-width: 115px;
      max-height: 200px;
      top: 0;
      right: 0;
    }
  }
`;

const RoundOverlay = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
  z-index: 10;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 6px 12px;
  }
`;

const ModeOverlay = styled.div`
  position: absolute;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 215, 0, 0.8);
  color: black;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;
  z-index: 10;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 4px 10px;
  }
`;

const TopRightOverlay = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 20;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const CountdownBox = styled.div`
  background: rgba(0,0,0,0.5);
  color: #fff;
  font-size: 0.95rem;
  font-weight: bold;
  border-radius: 12px;
  padding: 3px 10px;
  min-width: 28px;
  text-align: center;
`;

const SkipButton = styled.button`
  background: transparent;
  color: #e0e0e0;
  font-weight: 600;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 0.95rem;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.15s, border 0.15s;
  &:hover {
    opacity: 1;
    border: 1.5px solid #bdbdbd;
    color: #fff;
  }
`;

//max-height: 900px;

const VideoPlayer = ({ onVideoEnd, onMiniSize, forceMinimized = false, videoUrl, onForcePlay }) => {
  const endedRef = useRef(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [countdown, setCountdown] = useState(5);
  const [showSkip, setShowSkip] = useState(false);
  const [isVideoActuallyPlaying, setIsVideoActuallyPlaying] = useState(false);
  const [hasStartedCountdown, setHasStartedCountdown] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const countdownTimer = useRef(null);
  const skipHandled = useRef(false);
  const playerRef = useRef(null);


  const urlObj = new URL(videoUrl);
  let videoId = urlObj.searchParams.get('v');
  // Shorts URL 지원
  if (!videoId) {
    const paths = urlObj.pathname.split('/');
    videoId = paths[paths.length - 1];
  }

  // 사용자 상호작용 시 유튜브 재생 강제
  // const forcePlay = () => {
  //   if (playerRef.current) {
  //     try {
  //       const yt = playerRef.current.getInternalPlayer();
  //       if (yt && typeof yt.playVideo === 'function') {
  //         //console.log('Attempting to force play video...');
  //         // iOS에서는 뮤트 상태로 재생 시도, 안드로이드/데스크탑에서는 소리와 함께
  //         if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
  //           //yt.mute();
  //           yt.playVideo();
  //         } else {
  //           yt.unMute();
  //           yt.playVideo();
  //         }
  //       }
  //     } catch (error) {
  //       console.log('Force play failed:', error);
  //     }
  //   }
  // };

  // 외부에서 forcePlay 호출할 수 있도록 ref 등록
  // useEffect(() => {
  //   if (onForcePlay) {
  //     onForcePlay(() => {
  //       forcePlay();
  //     });
  //   }
  // }, [onForcePlay]);

  // VideoPlayer가 마운트되면 즉시 유튜브 재생 시도
  // useEffect(() => {
  //   if (videoUrl && !forceMinimized) {
  //     // DOM 렌더링 완료 후 재생 시도
  //     const timer = setTimeout(() => {
  //       forcePlay();
  //     }, 200);
      
  //     return () => clearTimeout(timer);
  //   }
  // }, [videoUrl, forceMinimized]);

  // 사용자 상호작용 이벤트 리스너 (iOS 자동재생 대응)
  // useEffect(() => {
  //   const handleUserInteraction = () => {
  //     if (!forceMinimized) {
  //       setTimeout(() => {
  //         forcePlay();
  //       }, 100);
  //     }
  //   };

    // 이벤트 리스너 추가 (필수 이벤트만)
  //   const events = ['touchstart', 'click', 'keydown'];
    
  //   events.forEach(event => {
  //     document.addEventListener(event, handleUserInteraction, { once: true });
  //   });

  //   return () => {
  //     events.forEach(event => {
  //       document.removeEventListener(event, handleUserInteraction);
  //     });
  //   };
  // }, [forceMinimized]);

  useEffect(() => {
    if (forceMinimized) {
      setIsMinimized(true);
      setIsMuted(true);
    }
  }, [forceMinimized]);

  // 1. 카운트다운 시작 트리거
  useEffect(() => {
    if (isVideoActuallyPlaying && !hasStartedCountdown && !isMinimized && !forceMinimized) {
      setHasStartedCountdown(true);
      setCountdown(5);
      setShowSkip(false);
      skipHandled.current = false;
    }
  }, [isVideoActuallyPlaying, hasStartedCountdown, isMinimized, forceMinimized]);

  // 2. 카운트다운 타이머
  useEffect(() => {
    if (!hasStartedCountdown) return;
    if (countdown === 0) {
      setShowSkip(true);
      setTimeout(() => {
        handleSkip();
      }, 500);
      return;
    }
    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [hasStartedCountdown, countdown]);

  // 영상이 실제로 플레이 시작되면 카운트다운 시작
  const handleStart = () => {
    // 실제 재생 상태는 onProgress에서 확인
  };

  // 영상 진행 상태를 통해 실제 재생 여부 확인
  const handleProgress = (state) => {
    // 영상이 실제로 재생되고 있는지 확인 (playedSeconds가 증가하고 있음)
    if (!isVideoActuallyPlaying && state.playedSeconds > 0 && state.loadedSeconds > 0) {
      setIsVideoActuallyPlaying(true);
    }
    
    if (!forceMinimized) {
      // 영상이 끝나기 1초 전에 미니사이즈로 전환
      if (!isMinimized && state.duration && (state.duration - state.playedSeconds <= 1)) {
        setIsMinimized(true);
        setIsMuted(true);
        onMiniSize && onMiniSize();
      }
      if (!endedRef.current && state.playedSeconds > 0 && state.loadedSeconds > 0 && state.playedSeconds >= state.loadedSeconds - 2) {
        endedRef.current = true;
        handleEnded();
      }
    }
  };

  // 스킵 버튼 클릭 또는 영상 끝나면 미니사이즈 전환
  const handleSkip = () => {
    if (skipHandled.current) return;
    skipHandled.current = true;
    setIsMinimized(true);      // 카운트다운 끝나면 미니사이즈 전환
    setIsMuted(true);
    onMiniSize && onMiniSize();
  };

  const handleEnded = () => {
    if (skipHandled.current) return;
    skipHandled.current = true;
    setIsMinimized(true);
    setIsMuted(true);
    onMiniSize && onMiniSize();
  };

  // 비디오 URL이 변경되면 상태 리셋
  useEffect(() => {
    setIsVideoActuallyPlaying(false);
    setHasStartedCountdown(false);
    setCountdown(5);
    setShowSkip(false);
    skipHandled.current = false;
    endedRef.current = false;
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
    }
  }, [videoUrl]);

  return (
    <VideoContainer style={{minHeight: 0, height: 'auto', flex: 1}}>
      <PlayerWrapper className={isMinimized || forceMinimized ? 'minimized' : ''}>
        {/* 오른쪽 상단 카운트다운/스킵 - 실제 재생 중일 때만 표시 */}
        {!(isMinimized || forceMinimized) && isVideoActuallyPlaying && hasStartedCountdown && (
          <TopRightOverlay>
            {countdown > 0 && <CountdownBox>{countdown}</CountdownBox>}
            {/* {showSkip && <SkipButton onClick={handleSkip}>SKIP</SkipButton>} */}
          </TopRightOverlay>
        )}
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          width="100%"
          height="100%"
          playing={false}
          controls={false}
          loop={true}
          muted={isMuted || forceMinimized}
          volume={isMuted || forceMinimized ? 0 : 0.5}
          onEnded={!forceMinimized ? handleEnded : undefined}
          onProgress={handleProgress}
          onStart={handleStart}
          onReady={() => {
            //console.log('Video player ready, attempting to play...');
            //const yt = playerRef.current?.getInternalPlayer();
            //setIsMuted(true);
            //yt.unMute();
            // iOS에서는 뮤트 상태 유지, 안드로이드/데스크탑에서는 뮤트 해제
            // if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
            //   setIsMuted(true);
            //   if (yt && typeof yt.mute === 'function') {
            //     yt.mute();
            //   }
            // } else {
            //   // 안드로이드/데스크탑에서는 소리 활성화
            //   setIsMuted(false);
            //   if (yt && typeof yt.unMute === 'function') {
            //     yt.unMute();
            //   }
            // }
            
            // if (!forceMinimized) {
            //   forcePlay();
            // }
          }}
          playsinline={true}
          config={{
            youtube: {
              playerVars: {
                autoplay: 0,
                mute: 0,
                controls: 0,
                disablekb: 1, /// 키보드 컨트롤 비활성화
                fs: 0,
                iv_load_policy: 3,
                modestbranding: 1,
                rel: 0,
                showinfo: 0,
                start: 0,
                end: 0,
                playsinline: 1,
                origin: window.location.origin,
                enablejsapi: 1,
                widget_referrer: window.location.origin,
                host: 'https://www.youtube.com',
                cc_load_policy: 0,
                color: 'white',
                hl: 'ko',
                loop: 1,
                playlist: videoId
              }
            }
          }}
          style={{
            objectFit: 'cover',
            borderRadius: isMinimized || forceMinimized ? '8px' : '12px',
            boxSizing: 'border-box',
            pointerEvents: (isMinimized || forceMinimized) ? 'none' : 'auto'
          }}
        />
      </PlayerWrapper>
    </VideoContainer>
  );
};

export default VideoPlayer; 